import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { redirectPreservingQuery } from '#app/utils/redirect.server.ts'
import { Link, useLoaderData, useLocation } from '@remix-run/react'
import { useEffect } from 'react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	GoogleRatingBadge,
	ReviewQuotes,
} from '#app/components/google-reviews.tsx'
import { MarkdownContent } from '#app/components/markdown-content.js'
import { PricingSection } from '#app/components/pricing-table.js'
import { ServiceCardGrid } from '#app/components/service-card-grid.js'
import { Icon } from '#app/components/ui/icon.tsx'
import { useBlvdUrl } from '#app/utils/blvd-context.tsx'
import { writeLastBookingServiceHint } from '#app/utils/booking-source-hints.ts'
import { type ServicePageSection } from '#app/utils/location-service-data.server.js'
import { getPricingForSlug } from '#app/utils/pricing.js'
import {
	getReviewHighlights,
	type FeaturedReview,
	type ReviewSummary,
} from '#app/utils/reviews.server.ts'
import { getSocialMetas } from '#app/utils/seo.ts'
import {
	getPage,
	getChildren,
	getAncestors,
	getSiblings,
	type SitePage,
} from '#app/utils/site-pages.server.js'
import {
	BreadcrumbJsonLd,
	FAQJsonLd,
	ServiceCheckMarks,
	ServiceFAQ,
	ServiceHeader,
	ServiceJsonLd,
	ServiceLayout,
	ServiceParagraph,
} from './_services+/__service-layout'

/**
 * Strip a location prefix from a slug.
 * "knoxville-botox" → "botox"
 * "farragut-filler/lip-filler" → "filler/lip-filler"
 * "knoxville-med-spa" → null (special case, handled by redirect routes)
 */
function stripLocationPrefix(slug: string): { basePath: string } | null {
	const match = slug.match(/^(knoxville|farragut)-(.+)$/)
	if (!match) return null
	const remainder = match[2]!
	return { basePath: remainder }
}

type LoaderData = {
	page: SitePage
	children: SitePage[]
	ancestors: SitePage[]
	siblings: SitePage[]
	markdown: string
	reviews: FeaturedReview[]
	reviewSummary: ReviewSummary
}

function getTreatmentLabel(pageName: string) {
	switch (pageName) {
		case 'Injectables':
			return 'Injectable treatment'
		case 'Laser Services':
			return 'Laser treatment'
		case 'Weight Loss':
			return 'Medical weight loss'
		default:
			return `${pageName} treatment`
	}
}

export async function loader({ params, request }: LoaderFunctionArgs) {
	const splat = params['*'] ?? ''

	// Directory sites copy our GBP link with the "?" percent-encoded
	// (/%3Futm_campaign=gmb), which lands here as a 404 that Google then
	// indexes. 301 to the decoded URL so the bad copies deindex and any link
	// equity flows to the real page.
	{
		const rawPath = new URL(request.url).pathname
		if (/%3F/i.test(rawPath)) {
			const url = new URL(request.url)
			const decodedPath = rawPath.replace(/%3F/i, '?')
			const extraQuery = url.search ? `&${url.search.slice(1)}` : ''
			return new Response(null, {
				status: 301,
				headers: { Location: `${decodedPath}${extraQuery}` },
			})
		}
	}

	if (splat === 'microneedling/face') {
		return redirectPreservingQuery(request, '/microneedling/facial', {
			status: 301,
		})
	}

	if (splat === 'telehealth-weight-loss') {
		return redirectPreservingQuery(request, '/weight-loss', { status: 301 })
	}

	if (splat === 'medical-weight-loss-telehealth') {
		return redirectPreservingQuery(request, '/weight-loss', { status: 301 })
	}

	// 301 redirect: old location-prefixed service URLs → base service URLs
	// e.g. /knoxville-botox → /botox, /farragut-filler/lip-filler → /filler/lip-filler
	const stripped = stripLocationPrefix(splat)
	if (stripped) {
		// Special case: knoxville-med-spa and farragut-med-spa redirect to location pages
		if (stripped.basePath === 'med-spa') {
			const locId = splat.startsWith('knoxville') ? 'bearden' : 'farragut'
			return redirectPreservingQuery(request, `/${locId}`, { status: 301 })
		}
		return redirectPreservingQuery(request, `/${stripped.basePath}`, {
			status: 301,
		})
	}

	// 301 redirect: old hierarchical URLs → canonical root slugs
	// e.g. /laser-services/everesse/face → /everesse/face,
	//      /injectables/kybella → /kybella
	if (splat.includes('/') && !getPage(splat)) {
		const segments = splat.split('/')
		for (let i = 1; i < segments.length; i++) {
			const candidate = segments.slice(i).join('/')
			const candidatePage = getPage(candidate)
			if (candidatePage?.enabled) {
				return redirectPreservingQuery(request, `/${candidate}`, {
					status: 301,
				})
			}
		}
	}

	// Direct match in site-pages → render the service page
	const page = getPage(splat)
	if (page && page.enabled) {
		const children = getChildren(splat)
		const ancestors = getAncestors(splat)
		const siblings = getSiblings(splat)
		const { reviews, summary: reviewSummary } = await getReviewHighlights(3)

		return json<LoaderData>({
			page,
			children,
			ancestors,
			siblings,
			markdown: page.content,
			reviews,
			reviewSummary,
		})
	}

	throw new Response('Not found', { status: 404 })
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
	// The loader's redirect branches degrade the inferred data type to `{}`,
	// so narrow by hand; the runtime shape on the rendering branch is
	// LoaderData either way.
	const page = (data as unknown as Partial<LoaderData> | undefined)?.page ?? null
	if (!page) return [{ title: 'Not Found | Sarah Hitchcox Aesthetics' }]
	return getSocialMetas({
		title: page.title,
		description: page.metaDescription,
		pathname: location.pathname,
	})
}

export default function DynamicPage() {
	const {
		page,
		children,
		ancestors,
		siblings,
		markdown,
		reviews,
		reviewSummary,
	} = useLoaderData<LoaderData>()
	const blvdUrl = useBlvdUrl()

	useEffect(() => {
		writeLastBookingServiceHint({
			label: page.name,
			path: `/${page.path}`,
			preferredLocationId: null,
			search: [page.name, ...(ancestors ?? []).map(ancestor => ancestor.name)]
				.join(' ')
				.trim(),
		})
	}, [ancestors, page.name, page.path])

	// Carousel images: use heroImages (before/after pairs) from frontmatter
	const imgs = page.heroImages ?? []

	const childCards = (children ?? [])
		.filter(c => c.enabled)
		.map(c => ({
			slug: c.path,
			serviceName: c.name,
			treatmentLabel: getTreatmentLabel(page.name),
			shortDescription: c.shortDescription,
			heroImage: c.heroImage,
		}))

	const renderSection = (section: ServicePageSection, index: number) => {
		switch (section.type) {
			case 'text-block':
				return (
					<div key={index} className="space-y-4">
						{section.title && <ServiceHeader>{section.title}</ServiceHeader>}
						<ServiceParagraph>{section.content}</ServiceParagraph>
					</div>
				)
			case 'features-grid':
				return (
					<div key={index} className="space-y-4">
						{section.title && <ServiceHeader>{section.title}</ServiceHeader>}
						<ServiceCheckMarks bulletPoints={section.items} />
					</div>
				)
			case 'faq-accordion':
				return (
					<div key={index} className="space-y-4">
						{section.title && <ServiceHeader>{section.title}</ServiceHeader>}
						<ServiceFAQ faq={section.items} />
					</div>
				)
			case 'cta-banner':
				return (
					<div
						key={index}
						className="my-8 rounded-xl bg-gray-900 p-8 text-center text-white shadow-lg"
					>
						<h3 className="mb-4 text-2xl font-bold">{section.title}</h3>
						<p className="mb-6 text-lg text-gray-300">{section.text}</p>
						<a
							href={section.link}
							className="inline-block rounded-md bg-white px-8 py-3 font-semibold text-black transition hover:bg-gray-200"
						>
							{section.buttonText}
						</a>
					</div>
				)
			case 'testimonials':
				return (
					<div key={index} className="space-y-4">
						<ServiceHeader>What Our Clients Say</ServiceHeader>
						<div className="grid gap-6 md:grid-cols-2">
							{section.items.map((item, i) => (
								<div key={i} className="rounded-lg bg-card p-6 shadow-sm">
									<p className="mb-4 italic text-muted-foreground">"{item.quote}"</p>
									<p className="font-semibold text-foreground">- {item.author}</p>
								</div>
							))}
						</div>
					</div>
				)
			default:
				return null
		}
	}

	// Breadcrumb: Home -> ancestors (reversed) -> current
	const breadcrumbItems = [
		{ path: '/', name: 'Home' },
		...[...(ancestors ?? [])].reverse().map(a => ({
			path: `/${a.path}`,
			name: a.name,
		})),
	]

	const isStatewide = page.statewide === true
	const title = isStatewide
		? page.name
		: page.name.toLowerCase().includes('knoxville')
			? page.name
			: `${page.name} Knoxville`

	const hasPricing = (getPricingForSlug(page.path)?.length ?? 0) > 0

	return (
		<ServiceLayout
			title={title}
			description={page.tagline}
			imgs={imgs}
			ratingBadge={<GoogleRatingBadge summary={reviewSummary} />}
			showPricingButton={hasPricing}
		>
			<ServiceJsonLd
				name={page.name}
				description={page.metaDescription}
				url={`https://hitchcoxaesthetics.com/${page.path}`}
			/>

			<BreadcrumbJsonLd
				items={[...breadcrumbItems, { name: page.name, path: `/${page.path}` }]}
			/>

			{page.faq && page.faq.length > 0 && (
				<FAQJsonLd faq={page.faq as { question: string; answer: string }[]} />
			)}

			{/* Breadcrumb: Home / Category / Current */}
			<nav className="mb-6 text-sm text-muted-foreground">
				{breadcrumbItems.map(item => (
					<span key={item.path}>
						<Link
							to={item.path}
							prefetch="intent"
							className="hover:text-primary hover:underline"
						>
							{item.name}
						</Link>
						<span className="mx-2">/</span>
					</span>
				))}
				<span className="font-medium text-foreground">{page.name}</span>
			</nav>

			{/* Main Content */}
			<MarkdownContent content={markdown} />

			{/* Ancestor Links (keyword-rich, in content body) */}
			{(ancestors ?? []).length > 0 ? (
				<div className="mt-8 rounded-lg border border-border bg-card p-6">
					<h3 className="mb-4 text-lg font-semibold text-foreground">
						Explore More {page.name} Treatments
					</h3>
					<p className="text-muted-foreground">
						{page.name} is part of our{' '}
						{(ancestors ?? []).map((ancestor, i) => (
							<span key={ancestor.path}>
								<Link
									to={`/${ancestor.path}`}
									className="font-medium text-primary hover:underline"
								>
									{isStatewide
										? `${ancestor.name.toLowerCase()} treatments in Tennessee`
										: `${ancestor.name.toLowerCase()} treatments in Knoxville`}
								</Link>
								{i < (ancestors ?? []).length - 1 && ' under our '}
							</span>
						))}{' '}
						{isStatewide
							? 'at Sarah Hitchcox Aesthetics. Browse our full range of services to find the right treatment for your goals anywhere in Tennessee.'
							: 'at Sarah Hitchcox Aesthetics in Knoxville, TN. Browse our full range of services to find the right treatment for your goals.'}
					</p>
				</div>
			) : null}

			{/* Dynamic Sections */}
			{page.sections?.map((section, index) => renderSection(section, index))}

			{/* Reviews */}
			{reviews.length > 0 ? (
				<ReviewQuotes reviews={reviews} summary={reviewSummary} />
			) : null}

			{/* Pricing */}
			<div id="pricing">
				<PricingSection serviceSlug={page.path} />
			</div>

			{/* Sub-service Grid (for pages with children) */}
			{childCards.length > 0 && (
				<div className="mt-12">
					<ServiceHeader>
						{isStatewide
							? `${page.name} Program Options in Tennessee`
							: `${page.name} Treatments in Knoxville`}
					</ServiceHeader>
					<div className="mt-6">
						<ServiceCardGrid services={childCards} variant="thumbnail" />
					</div>
				</div>
			)}

			{/* Sibling Links */}
			{(siblings ?? []).length > 0 && (
				<div className="mt-12 border-t border-gray-200 pt-8">
					<ServiceHeader>
						{isStatewide
							? 'Related Aesthetic Treatments in Tennessee'
							: 'Related Aesthetic Treatments in Knoxville'}
					</ServiceHeader>
					<div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{(siblings ?? []).map(sibling => (
							<Link
								key={sibling.path}
								to={`/${sibling.path}`}
								className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-all hover:shadow-md"
							>
								{sibling.heroImage && (
									<img
										src={sibling.heroImage}
										alt={sibling.name}
										className="h-16 w-16 rounded-lg object-cover"
										loading="lazy"
									/>
								)}
								<div>
									<h4 className="font-semibold text-foreground hover:text-primary">
										{sibling.name}
									</h4>
									<p className="text-sm text-muted-foreground">
										{sibling.shortDescription}
									</p>
								</div>
							</Link>
						))}
					</div>
				</div>
			)}

			{/* Location links */}
			{!isStatewide ? (
				<div className="mt-8 rounded-lg border border-border bg-card p-6">
					<p className="text-muted-foreground">
						Available at both of our Knoxville area locations:{' '}
						<Link
							to="/bearden"
							className="font-medium text-primary hover:underline"
						>
							Bearden (West Knoxville)
						</Link>
						{' and '}
						<Link
							to="/farragut"
							className="font-medium text-primary hover:underline"
						>
							Farragut
						</Link>
						.
					</p>
				</div>
			) : null}

			{/* CTA */}
			<div className="mt-12 flex flex-col items-center justify-center">
				<a
					href={blvdUrl}
					className="rounded-full bg-primary px-10 py-4 text-lg font-bold uppercase tracking-wider text-primary-foreground shadow-lg transition-all hover:-translate-y-1 hover:bg-primary/90 hover:shadow-xl"
				>
					{page.ctaText || 'Book Your Consultation'}
				</a>
			</div>
		</ServiceLayout>
	)
}

export function ErrorBoundary() {
	const location = useLocation()
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-3">
							<h1>We can't find this page:</h1>
							<pre className="whitespace-pre-wrap break-all text-body-lg">
								{location.pathname}
							</pre>
						</div>
						<Link to="/" className="text-body-md underline">
							<Icon name="arrow-left">Back to home</Icon>
						</Link>
					</div>
				),
			}}
		/>
	)
}
